import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ContactEditUpdateContactResponseInvitationResend,
  type ContactEditSessionInfo,
} from "@wellcheck/api-client";
import { Button } from "@wellcheck/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@wellcheck/components/ui/form";
import { Input } from "@wellcheck/components/ui/input";

import { isContactEditDuplicateEmailError } from "../api/contactEditNotAllowed";
import { useUpdateContact } from "../api/useUpdateContact";
import { buildContactSchema, type ContactFormInput } from "../data/contactSchema";

/**
 * savedMessageKey — 保存成功メッセージの i18n key を招待メール再送結果 (ADR-0041 Decision 6 の
 * 2026-09-02 追記) から決める。再送失敗時も連絡先自体は保存済みのため、汎用エラーではなく
 * 専用の案内文言にする。
 */
function savedMessageKey(
  invitationResend: ContactEditUpdateContactResponseInvitationResend,
): "form.savedResendSent" | "form.savedResendFailed" | "form.savedNotNeeded" {
  switch (invitationResend) {
    case ContactEditUpdateContactResponseInvitationResend.sent:
      return "form.savedResendSent";
    case ContactEditUpdateContactResponseInvitationResend.failed:
      return "form.savedResendFailed";
    default:
      return "form.savedNotNeeded";
  }
}

/**
 * ContactEditForm — 本人が自分の氏名・メールアドレスを確認・修正するフォーム
 * (ADR-0041 Decision 6)。
 *
 * 現在値を初期表示する。誤りを直す機能で現在値が見えないと要件を満たさないため
 * (ADR-0034 の「メールアドレスを表示しない」判断との非対称は ADR-0041 Decision 6 に記載)。
 *
 * mutation は呼び出し側 (ContactEditPage) が所有する。受付期限超過 (409) では見出しごと
 * 受付終了画面に差し替える必要があり、フォーム内では自分の外側の見出しを消せないため。
 */
export function ContactEditForm({
  session,
  mutation,
}: {
  session: ContactEditSessionInfo;
  mutation: ReturnType<typeof useUpdateContact>;
}) {
  const { t } = useTranslation("contactEdit");

  const schema = useMemo(
    () =>
      buildContactSchema({
        fullNameRequired: t("form.fullNameRequired"),
        emailRequired: t("form.emailRequired"),
        emailInvalid: t("form.emailInvalid"),
      }),
    [t],
  );

  const values = useMemo<ContactFormInput>(
    () => ({ fullName: session.fullName, email: session.email }),
    [session.fullName, session.email],
  );

  const form = useForm<ContactFormInput>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: { fullName: "", email: "" },
    values,
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((input) => mutation.mutate(input))}
        className="space-y-6"
        noValidate
      >
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.fullNameLabel")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.emailLabel")}</FormLabel>
              <FormControl>
                <Input type="email" inputMode="email" autoComplete="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <p className="text-sm text-muted-foreground">{t("form.notice")}</p>

        {mutation.isSuccess && !form.formState.isDirty && (
          <p role="status" className="text-sm font-medium text-foreground">
            {t(savedMessageKey(mutation.data.invitationResend))}
          </p>
        )}
        {mutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            {isContactEditDuplicateEmailError(mutation.error)
              ? t("form.duplicateEmailError")
              : t("form.saveError")}
          </p>
        )}

        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t("form.saving") : t("form.save")}
        </Button>
      </form>
    </Form>
  );
}
